// ---------------------------------------------------------------------------
// Shared crowd-pressure model for the School Holidays & Busyness page.
//
// This is the SINGLE source of truth for how weekly "crowd pressure" is
// computed and labelled. It is imported by:
//   - lib/conditions/busyness-data.ts   (server: builds the default heatbar)
//   - lib/conditions/timing.ts          (server: writes timing_scores -> /best-time-to-go + quiet weeks)
//   - app/research/school-holidays/*    (client: per-country recompute, ranking, labels)
//
// Keep this module CLIENT-SAFE: no Supabase, no next/cache, no server-only imports.
// The model: feeder-weighted school-holiday overlap + a public-holiday boost +
// a season-phase base-demand floor, squashed through a logarithmic curve.
// ---------------------------------------------------------------------------

import { getSeasonPhase } from "./seasons";
import { SEASON_PHASE_WEIGHTS } from "./crowd-prediction";

// Logarithmic compression constant. History: 3.5 -> 9 -> 4 (deliberate retunes).
// Tuned so a single dominant feeder on holiday reads ~0.7 and a multi-feeder
// Christmas reads ~0.95. Defined ONCE here so the curve cannot drift between
// the server, the timing pipeline and the client again.
export const CROWD_K = 4;

// Per public-holiday day, per feeder country: weight * this. Raised from the
// original 0.03 so feeder-country bank holidays (a real long-weekend driver)
// are not invisible after compression, while staying well below a school
// holiday's contribution.
export const PUBLIC_HOLIDAY_DAY_WEIGHT = 0.08;

const NATIONAL_REGION = "__national__";

export type PressureHolidayBar = {
  countryCode: string;
  region: string | null;
  holidayName: string;
  startDate: string;
  endDate: string;
};

export type WeekRange = { weekStart: string; weekEnd: string };

export type WeeklyPressure = {
  weekStart: string;
  weekEnd: string;
  pressure: number; // 0-1 compressed
  overlapCount: number; // distinct countries on holiday
  activeHolidays: PressureHolidayBar[];
};

export type PressureInputs = {
  bars: PressureHolidayBar[];
  // Returns the feeder weight (share of visitors, 0-1) for a country at the
  // destination being scored. Non-feeders return 0.
  weightFor: (countryCode: string) => number;
  // country_code -> ISO date strings of public holidays in range.
  publicHolidaysByCountry?: Record<string, string[]>;
  seasonStart: string;
  seasonEnd: string;
  // Distinct region count per country across the season (FR=3, NL=3, DE=3 mapped,
  // region-NULL countries=1). Derived from `bars` if omitted.
  regionCounts?: Map<string, number>;
  // Toggle the season-phase base-demand floor (default on).
  withSeasonPhase?: boolean;
};

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

export function overlapFraction(
  weekStart: string,
  weekEnd: string,
  holStart: string,
  holEnd: string
): number {
  const ws = new Date(weekStart).getTime();
  const we = new Date(weekEnd).getTime();
  const oStart = Math.max(ws, new Date(holStart).getTime());
  const oEnd = Math.min(we, new Date(holEnd).getTime());
  if (oStart > oEnd) return 0;
  const DAY = 86_400_000;
  // Inclusive whole-day counts on the generateWeekStarts 7-day grid.
  return ((oEnd - oStart) / DAY + 1) / ((we - ws) / DAY + 1);
}

export function durationDampening(startDate: string, endDate: string): number {
  // Long holidays (e.g. a 6-week summer) spread their crowd thinner across any
  // single week, so a fully-overlapped week of a long holiday contributes less
  // than a fully-overlapped week of a short half-term. 7-day reference holiday
  // gets 1.0; a 14-day ~0.73; a 42-day ~0.45.
  const days =
    (new Date(endDate).getTime() - new Date(startDate).getTime()) / 86_400_000 + 1;
  return Math.min(1, Math.pow(7 / days, 0.45));
}

export function compressPressure(rawLinearSum: number): number {
  return 1 - Math.exp(-CROWD_K * rawLinearSum);
}

// Distinct region count per country. region=null collapses to a single
// national bucket, so country-level feeders are unaffected by the share split.
export function countryRegionCounts(
  bars: PressureHolidayBar[]
): Map<string, number> {
  const regionsByCountry = new Map<string, Set<string>>();
  for (const bar of bars) {
    const set = regionsByCountry.get(bar.countryCode) ?? new Set<string>();
    set.add(bar.region ?? NATIONAL_REGION);
    regionsByCountry.set(bar.countryCode, set);
  }
  const counts = new Map<string, number>();
  for (const [cc, set] of regionsByCountry) counts.set(cc, set.size);
  return counts;
}

// ---------------------------------------------------------------------------
// Raw (pre-compression) pressure for a single week.
//
// Per country we apportion by the share of its school zones actually on
// holiday: sum over regions of max(frac*damp) within that region, divided by
// the country's total region count. So when only one French zone is off the
// week, France contributes ~1/3 of its feeder weight (not the full weight a
// naive max-across-zones would give) -- which restores the quiet sub-weeks
// that zone staggering deliberately creates. Duplicate bilingual bars within a
// region collapse via the per-region max, so the sum never double-counts.
// ---------------------------------------------------------------------------

export function weekRawPressure(
  weekStart: string,
  weekEnd: string,
  inp: PressureInputs
): number {
  const regionCounts = inp.regionCounts ?? countryRegionCounts(inp.bars);
  const perCountryRegion = new Map<string, Map<string, number>>();

  for (const bar of inp.bars) {
    if (bar.startDate <= weekEnd && bar.endDate >= weekStart) {
      const value =
        overlapFraction(weekStart, weekEnd, bar.startDate, bar.endDate) *
        durationDampening(bar.startDate, bar.endDate);
      const regionKey = bar.region ?? NATIONAL_REGION;
      let regions = perCountryRegion.get(bar.countryCode);
      if (!regions) {
        regions = new Map();
        perCountryRegion.set(bar.countryCode, regions);
      }
      regions.set(regionKey, Math.max(regions.get(regionKey) ?? 0, value));
    }
  }

  let raw = 0;
  for (const [cc, regions] of perCountryRegion) {
    const weight = inp.weightFor(cc);
    if (weight <= 0) continue;
    const denom = regionCounts.get(cc) ?? 1;
    let regionShareSum = 0;
    for (const v of regions.values()) regionShareSum += v;
    const countryFraction = Math.min(1, regionShareSum / denom);
    raw += weight * countryFraction;
  }

  // Public-holiday boost (feeder-country bank holidays in the week).
  if (inp.publicHolidaysByCountry) {
    for (const cc in inp.publicHolidaysByCountry) {
      const weight = inp.weightFor(cc);
      if (weight <= 0) continue;
      for (const date of inp.publicHolidaysByCountry[cc]) {
        if (date >= weekStart && date <= weekEnd) {
          raw += weight * PUBLIC_HOLIDAY_DAY_WEIGHT;
        }
      }
    }
  }

  // Season-phase base demand: lifts the floor so genuinely busy non-holiday
  // peak weeks never read as empty. Holiday terms stay dominant.
  if (inp.withSeasonPhase !== false) {
    raw += SEASON_PHASE_WEIGHTS[getSeasonPhase(weekStart, inp.seasonStart, inp.seasonEnd)];
  }

  return raw;
}

// Deduplicate bilingual/duplicate bars for display, keyed by country|region|
// dates. Prefers the more English-looking name so the Gantt and active-holiday
// lists show one canonical label.
const ENGLISH_HINTS = [
  "holiday",
  "christmas",
  "new year",
  "easter",
  "spring",
  "summer",
  "autumn",
  "winter",
  "half",
  "carnival",
  "mid",
  "break",
];

function englishness(name: string): number {
  const lower = name.toLowerCase();
  return ENGLISH_HINTS.reduce((n, hint) => (lower.includes(hint) ? n + 1 : n), 0);
}

export function dedupeBars(bars: PressureHolidayBar[]): PressureHolidayBar[] {
  const byKey = new Map<string, PressureHolidayBar>();
  for (const bar of bars) {
    const key = `${bar.countryCode}|${bar.region ?? ""}|${bar.startDate}|${bar.endDate}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, bar);
    } else if (englishness(bar.holidayName) > englishness(existing.holidayName)) {
      byKey.set(key, bar);
    }
  }
  return Array.from(byKey.values());
}

export function computeWeeklyPressure(
  weeks: WeekRange[],
  inp: PressureInputs
): WeeklyPressure[] {
  const regionCounts = inp.regionCounts ?? countryRegionCounts(inp.bars);
  const resolved: PressureInputs = { ...inp, regionCounts };

  return weeks.map((week) => {
    const active = dedupeBars(
      inp.bars.filter(
        (bar) => bar.startDate <= week.weekEnd && bar.endDate >= week.weekStart
      )
    );
    const overlapCount = new Set(active.map((b) => b.countryCode)).size;
    return {
      weekStart: week.weekStart,
      weekEnd: week.weekEnd,
      pressure: compressPressure(weekRawPressure(week.weekStart, week.weekEnd, resolved)),
      overlapCount,
      activeHolidays: active,
    };
  });
}

// ---------------------------------------------------------------------------
// Shared labels + colours (one vocabulary across every widget on the page)
// ---------------------------------------------------------------------------

type PressureBand = {
  min: number;
  label: string;
  colour: string;
  textColour: string;
};

const PRESSURE_BANDS: PressureBand[] = [
  { min: 0.7, label: "Rammed", colour: "bg-red-400", textColour: "text-red-700" },
  { min: 0.5, label: "Busy", colour: "bg-orange-400", textColour: "text-orange-700" },
  { min: 0.3, label: "Moderate", colour: "bg-amber-400", textColour: "text-amber-700" },
  { min: 0.15, label: "Quiet", colour: "bg-lime-500", textColour: "text-lime-700" },
  { min: 0, label: "Very quiet", colour: "bg-emerald-500", textColour: "text-emerald-700" },
];

function bandFor(pressure: number): PressureBand {
  return PRESSURE_BANDS.find((b) => pressure >= b.min) ?? PRESSURE_BANDS[PRESSURE_BANDS.length - 1];
}

export function pressureLabel(pressure: number): string {
  return bandFor(pressure).label;
}

export function pressureColour(pressure: number): string {
  return bandFor(pressure).colour;
}

export function pressureTextColour(pressure: number): string {
  return bandFor(pressure).textColour;
}

// Legend entries (deduped to the distinct bands), ordered quiet -> busy.
export const PRESSURE_LEGEND = [...PRESSURE_BANDS]
  .reverse()
  .map((b) => ({ label: b.label, colour: b.colour }));
